import React from 'react';
import { useRouteError, useNavigate, isRouteErrorResponse } from 'react-router-dom';
import { PrimaryButton } from '@/components/ui/Buttons/Primary';
import { SecondaryButton } from '@/components/ui/Buttons/Secondary';
import designTokens from '@/design-tokens';

interface RouteError {
  status?: number;
  statusText?: string;
  data?: string;
  message?: string;
  stack?: string;
}

export const RouteErrorPage: React.FC = () => {
  const error = useRouteError() as RouteError;
  const navigate = useNavigate();

  // Determine error details
  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again.';
  let showDetails = false;

  if (isRouteErrorResponse(error)) {
    // Router-specific errors (404, etc.)
    title = `${error.status} ${error.statusText}`;
    message = error.data || 'The page you requested could not be found.';
  } else if (error instanceof Error) {
    // JavaScript errors
    title = 'Application Error';
    message = error.message || 'An unexpected error occurred.';
    showDetails = process.env.NODE_ENV === 'development';
  }

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: designTokens['surface/greyscale/fog'] || '#f9fafb',
    padding: '24px'
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: designTokens['surface/greyscale/clear'] || '#ffffff',
    borderRadius: designTokens['radius/lgr'] || '24px',
    padding: '48px',
    maxWidth: '600px',
    width: '100%',
    textAlign: 'center',
    boxShadow: 'none',
    border: `1px solid ${designTokens['border/color/default'] || '#e5e7eb'}`
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: designTokens['font/family/bold'] || 'Montserrat, system-ui, sans-serif',
    fontWeight: designTokens['font/weight/bold'] || '700',
    fontSize: designTokens['font/size/xl'] || '1.25rem',
    lineHeight: designTokens['font/line-height/lg'] || '1.75rem',
    color: designTokens['text/primary'] || '#000000',
    marginBottom: '16px'
  };

  const messageStyle: React.CSSProperties = {
    fontFamily: designTokens['font/family/medium'] || 'Montserrat, system-ui, sans-serif',
    fontSize: designTokens['font/size/base'] || '1rem',
    lineHeight: designTokens['font/line-height/md'] || '1.5rem',
    color: designTokens['text/secondary'] || '#6b7280',
    marginBottom: '32px'
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  };

  const detailsStyle: React.CSSProperties = {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: designTokens['surface/greyscale/mist'] || '#f3f4f6',
    borderRadius: designTokens['radius/small'] || '8px',
    textAlign: 'left',
    fontSize: designTokens['font/size/sm'] || '0.875rem',
    fontFamily: 'Monaco, Consolas, monospace',
    color: designTokens['text/tertiary'] || '#374151',
    overflow: 'auto',
    maxHeight: '200px'
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Error Icon */}
        <div style={{ marginBottom: '24px' }}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            style={{ margin: '0 auto', display: 'block' }}
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke={designTokens['surface/error/primary'] || '#ef4444'}
              strokeWidth="2"
              fill={designTokens['surface/error/light'] || '#fef2f2'}
            />
            <path
              d="M12 8v4"
              stroke={designTokens['surface/error/primary'] || '#ef4444'}
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M12 16h.01"
              stroke={designTokens['surface/error/primary'] || '#ef4444'}
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {/* Error Content */}
        <h1 style={titleStyle}>{title}</h1>
        <p style={messageStyle}>{message}</p>

        {/* Action Buttons */}
        <div style={buttonContainerStyle}>
          <PrimaryButton onClick={handleGoHome}>
            Go to Dashboard
          </PrimaryButton>
          <SecondaryButton onClick={handleGoBack}>
            Go Back
          </SecondaryButton>
          <SecondaryButton onClick={handleReload}>
            Reload Page
          </SecondaryButton>
        </div>

        {/* Development Details */}
        {showDetails && error instanceof Error && (
          <details style={detailsStyle}>
            <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
              Error Details (Development)
            </summary>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {error.stack || error.message}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};